import React, { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { Currency } from 'components/_basic/Currency/Currency';
import { FormTable } from 'components/_basic/Form/FormTableComponent';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';

import { concatWithDot } from 'helpers/string';

import { GeneralAndAdministrativeErrorMessage, GeneralAndAdministrativeTotal } from './components';
import { defaultColumns } from './constants';
import { useFocusForm } from '../hooks';
import {
  defaultGeneralAndAdministrativeItem,
  FIRST_COLUMN_MAXLENGTH,
  FIRST_COLUMN_WIDTH,
  GENERAL_AND_ADMINISTRATIVE_FORM_NAMES,
  generalAndAdministrativeColumns,
} from './utils/form';
import { MONTHLY_EXPENSES_FORM_CHUNKS, MONTHLY_EXPENSES_FORM_NAMES } from '../MonthlyExpensesForm/utils/form';
import { EXPENSE_TYPE_NAMES, generalAndAdministrativePrefix, monthlyExpensesPrefix } from '../utils/form';

export const GeneralAndAdministrativeForm: React.FC = () => {
  const { t } = useTranslation();

  const { control } = useFormContext();

  const [hasError, setHasError] = useState(false);

  const getDefaultColumnTitle = (index: number) => t(generalAndAdministrativeColumns[index].title);

  const handleErrorStateChange = (error: boolean) => setHasError(error);

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.PLANNED_EXPENSES,
  );

  const tableName = concatWithDot(
    generalAndAdministrativePrefix,
    GENERAL_AND_ADMINISTRATIVE_FORM_NAMES.GENERAL_AND_ADMINISTRATIVE_TABLE,
  );

  const { fields, remove: onRemoveRow, insert } = useFieldArray({ name: tableName, control });

  const focusForm = useFocusForm();

  const onInsertRow = (index: number) => insert(index, defaultGeneralAndAdministrativeItem);

  const scrollToElement = () => {
    focusForm(
      EXPENSE_TYPE_NAMES.MONTHLY_EXPENSES,
      concatWithDot(
        monthlyExpensesPrefix,
        MONTHLY_EXPENSES_FORM_NAMES[MONTHLY_EXPENSES_FORM_CHUNKS.GENERAL_AND_ADMINISTRATIVE].EXPENSES,
      ),
    );
  };

  return (
    <>
      <FormSectionCard
        collapsible
        disabled={!canAccess}
        title={t('financialPlan.plannedExpenses.generalAndAdministrative.tableDescription')}
      >
        <>
          <Currency />
          <FormSectionFilledCard>
            <FormTable
              name={tableName}
              rows={fields}
              columns={generalAndAdministrativeColumns}
              disabled={!canAccess}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              firstColumnLength={FIRST_COLUMN_MAXLENGTH}
              defaultColumns={defaultColumns}
              getDefaultColumnTitle={getDefaultColumnTitle}
              onRemoveRow={onRemoveRow}
              onInsertRow={onInsertRow}
              notAllowedToDeleteRowIndexes={[0, 1, 2]}
              needToConvertToNegative
            />

            <GeneralAndAdministrativeTotal
              name={tableName}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              columns={generalAndAdministrativeColumns}
              disabled={!canAccess}
              onErrorStateChange={handleErrorStateChange}
              scrollToElement={scrollToElement}
            />
          </FormSectionFilledCard>

          {hasError && <GeneralAndAdministrativeErrorMessage onClick={scrollToElement} />}
        </>
      </FormSectionCard>
    </>
  );
};
